import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { toast } from "sonner";
import { FileText, FileSpreadsheet, FileType, Filter, RotateCcw } from "lucide-react";

const STATUS_COLOR = {
  DELIVERED: "text-emerald-700",
  IN_TRANSIT: "text-blue-700",
  PICKED_UP: "text-amber-700",
  SCHEDULED: "text-gray-600",
  CANCELLED: "text-gray-400",
  REJECTED: "text-red-700",
};

export default function HaulingSummary() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ load_count: 0, total_tons: 0, total_amount: 0, delivered_count: 0 });
  const [projects, setProjects] = useState([]);
  const [haulers, setHaulers] = useState([]);
  const [filters, setFilters] = useState({ project_id: "", hauler_id: "", start: "", end: "" });
  const [loading, setLoading] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL;

  const query = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.append(k, v); });
    return p.toString();
  }, [filters]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const q = query;
      const { data } = await api.get(`/reports/hauling-summary${q ? `?${q}&format=json` : "?format=json"}`);
      setRows(data.rows || []);
      setMeta(data.meta || {});
    } catch (e) {
      toast.error("Failed to load hauling data");
    } finally { setLoading(false); }
  }, [query]);

  useEffect(() => {
    Promise.all([api.get("/projects"), api.get("/haulers")]).then(([p, h]) => {
      setProjects(p.data);
      setHaulers(h.data);
    });
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const reset = () => setFilters({ project_id: "", hauler_id: "", start: "", end: "" });

  const downloadUrl = (fmt) => `${backend}/api/reports/hauling-summary?${query ? query + "&" : ""}format=${fmt}`;

  return (
    <div>
      <PageHeader
        title="Hauling Summary"
        subtitle="Load × Project × Hauler × Site — export CSV / XLSX / PDF"
        actions={
          <div className="flex items-center gap-2">
            <a href={downloadUrl("csv")} target="_blank" rel="noreferrer" data-testid="download-hs-csv" className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 text-[11px] uppercase tracking-widest font-semibold hover:bg-gray-900 hover:text-white">
              <FileText className="w-3.5 h-3.5" /> CSV
            </a>
            <a href={downloadUrl("xlsx")} target="_blank" rel="noreferrer" data-testid="download-hs-xlsx" className="flex items-center gap-1.5 px-3 py-2 border border-emerald-500 text-emerald-700 bg-emerald-50 text-[11px] uppercase tracking-widest font-semibold hover:bg-emerald-500 hover:text-white">
              <FileSpreadsheet className="w-3.5 h-3.5" /> XLSX
            </a>
            <a href={downloadUrl("pdf")} target="_blank" rel="noreferrer" data-testid="download-hs-pdf" className="flex items-center gap-1.5 px-3 py-2 border border-orange-500 text-orange-700 bg-orange-50 text-[11px] uppercase tracking-widest font-semibold hover:bg-orange-500 hover:text-white">
              <FileType className="w-3.5 h-3.5" /> PDF
            </a>
          </div>
        }
      />
      <div className="p-8 space-y-6">
        {/* Filters */}
        <div className="bg-white border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-4 h-4 text-gray-500" />
            <div className="font-display font-bold">Filters</div>
            <button
              onClick={reset}
              data-testid="reset-hs-filters"
              className="ml-auto text-[10px] uppercase tracking-widest font-semibold text-gray-600 hover:text-gray-900 flex items-center gap-1"
            >
              <RotateCcw className="w-3 h-3" /> Reset
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="label-caps block mb-1.5">Project</label>
              <select value={filters.project_id} onChange={(e) => set("project_id", e.target.value)} className="w-full border border-gray-300 py-2 px-3 text-sm" data-testid="hs-project-filter">
                <option value="">All projects</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.code} · {p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label-caps block mb-1.5">Hauler</label>
              <select value={filters.hauler_id} onChange={(e) => set("hauler_id", e.target.value)} className="w-full border border-gray-300 py-2 px-3 text-sm" data-testid="hs-hauler-filter">
                <option value="">All haulers</option>
                {haulers.map((h) => <option key={h.id} value={h.id}>{h.company_name}</option>)}
              </select>
            </div>
            <div>
              <label className="label-caps block mb-1.5">From</label>
              <input type="date" value={filters.start} onChange={(e) => set("start", e.target.value)} className="w-full border border-gray-300 py-2 px-3 text-sm font-mono" data-testid="hs-start-filter" />
            </div>
            <div>
              <label className="label-caps block mb-1.5">To</label>
              <input type="date" value={filters.end} onChange={(e) => set("end", e.target.value)} className="w-full border border-gray-300 py-2 px-3 text-sm font-mono" data-testid="hs-end-filter" />
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI label="Loads" value={meta.load_count} testid="kpi-loads" />
          <KPI label="Delivered" value={meta.delivered_count} testid="kpi-delivered" />
          <KPI label="Total tons" value={(meta.total_tons || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })} suffix="t" testid="kpi-tons" />
          <KPI label="Billed" value={`$${(meta.total_amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`} testid="kpi-billed" />
        </div>

        {/* Table */}
        <div className="bg-white border border-gray-200 overflow-x-auto" data-testid="hs-table">
          <table className="w-full text-sm">
            <thead className="bg-gray-900 text-white">
              <tr>
                <Th>BOL</Th>
                <Th>Date</Th>
                <Th>Project</Th>
                <Th>Hauler</Th>
                <Th>Truck</Th>
                <Th>Pickup</Th>
                <Th>Dropoff</Th>
                <Th className="text-right">Tons</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="9" className="text-center py-8 text-gray-500">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan="9" className="text-center py-8 text-gray-500">No hauls match these filters.</td></tr>}
              {!loading && rows.map((r) => (
                <tr key={r.bol_number} className="odd:bg-gray-50 border-b border-gray-100" data-testid={`hs-row-${r.bol_number}`}>
                  <Td mono>{r.bol_number}</Td>
                  <Td mono>{r.created_at}</Td>
                  <Td>{r.project_code}</Td>
                  <Td>{r.hauler_name}</Td>
                  <Td mono>{r.truck_number}</Td>
                  <Td>{r.pickup_site_name}</Td>
                  <Td>{r.dropoff_site_name}</Td>
                  <Td mono className="text-right">{Number(r.weight_tons).toFixed(1)}</Td>
                  <Td><span className={`text-[10px] uppercase tracking-widest font-bold ${STATUS_COLOR[r.status] || "text-gray-700"}`}>{r.status}</span></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, suffix, testid }) {
  return (
    <div className="bg-white border border-gray-200 p-5" data-testid={testid}>
      <div className="label-caps mb-1">{label}</div>
      <div className="stat-value text-3xl text-gray-900 font-display font-black">
        {value}
        {suffix && <span className="text-gray-500 text-base font-normal ml-1">{suffix}</span>}
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`text-left px-3 py-2.5 text-[10px] uppercase tracking-widest font-semibold ${className}`}>{children}</th>;
}
function Td({ children, mono, className = "" }) {
  return <td className={`px-3 py-2 ${mono ? "font-mono text-xs" : "text-sm"} ${className}`}>{children}</td>;
}
